import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface LinkoutAddRequestPayload {
  correlationId: string;
  claimNumber: string;
}

/** Backend LinkoutRedirectResult (PascalCase or camelCase JSON). */
export interface LinkoutRedirectApiResponse {
  statusCode?: number;
  StatusCode?: number;
  status?: number | string;
  Status?: number | string;
  errorCode?: string;
  ErrorCode?: string;
  errorMessage?: string;
  ErrorMessage?: string;
  redirectUrl?: string;
  RedirectUrl?: string;
  url?: string;
  Url?: string;
  ssoErrorMessage?: string;
  SsoErrorMessage?: string;
  data?: {
    redirectUrl?: string;
    RedirectUrl?: string;
    url?: string;
    Url?: string;
    responseCode?: string;
    ResponseCode?: string;
  } | null;
}

const OPEN_ADD_DIALOG_KEY = 'pb_linkout_open_add_dialog';
const RETURN_KEY = 'pb_linkout_return';
const PENDING_DIALOG_KEY = 'pb_linkout_pending_add_request';
const DIALOG_OPENED_PREFIX = 'pb_linkout_dialog_opened_';

/**
 * Clears link-out session state without DI (safe to call from guards / logout).
 * LinkoutService.clearLinkoutFlowState delegates here.
 */
export function clearLinkoutFlowStateStorage(): void {
  sessionStorage.removeItem(RETURN_KEY);
  sessionStorage.removeItem(PENDING_DIALOG_KEY);
  sessionStorage.removeItem(OPEN_ADD_DIALOG_KEY);
  for (let i = sessionStorage.length - 1; i >= 0; i--) {
    const k = sessionStorage.key(i);
    if (k && k.startsWith(DIALOG_OPENED_PREFIX)) {
      sessionStorage.removeItem(k);
    }
  }
  try {
    localStorage.removeItem('linkoutEntryResolveUrl');
  } catch {
    /* ignore */
  }
}

/**
 * LinkoutRedirectStatus.Success — string "Success" or numeric 0 (adjust if your enum differs).
 */
export function isLinkoutRedirectSuccess(res: LinkoutRedirectApiResponse | null | undefined): boolean {
  if (!res) {
    return false;
  }
  const st = res.Status ?? res.status;
  if (st === null || st === undefined) {
    return false;
  }
  if (typeof st === 'string') {
    return st.toLowerCase() === 'success';
  }
  return st === 0;
}

export function getLinkoutRedirectErrorMessage(res: LinkoutRedirectApiResponse): string {
  const errorMessage = res.ErrorMessage ?? res.errorMessage;
  if (errorMessage != null && String(errorMessage).trim() !== '') {
    return String(errorMessage).trim();
  }
  const m = res.SsoErrorMessage ?? res.ssoErrorMessage;
  if (m != null && String(m).trim() !== '') {
    return String(m).trim();
  }
  return 'This link could not be opened. Please try again or contact support.';
}

export function getLinkoutRedirectNavigationUrl(res: LinkoutRedirectApiResponse): string {
  const raw =
    res.RedirectUrl ??
    res.redirectUrl ??
    res.Url ??
    res.url ??
    res.data?.RedirectUrl ??
    res.data?.redirectUrl ??
    res.data?.Url ??
    res.data?.url;
  return raw != null ? String(raw).trim() : '';
}

@Injectable({
  providedIn: 'root',
})
export class LinkoutService {
  constructor(private http: HttpClient) {}

  storeReturnUrl(url: string): void {
    sessionStorage.setItem(RETURN_KEY, url);
  }

  peekReturnUrl(): string | null {
    return sessionStorage.getItem(RETURN_KEY);
  }

  consumeReturnUrl(): string | null {
    const v = sessionStorage.getItem(RETURN_KEY);
    if (v) {
      sessionStorage.removeItem(RETURN_KEY);
    }
    return v;
  }

  /** Drop pending Add Request state (e.g. user cannot complete link-out on this route). */
  clearPendingAddRequest(): void {
    sessionStorage.removeItem(PENDING_DIALOG_KEY);
    sessionStorage.removeItem(OPEN_ADD_DIALOG_KEY);
  }

  /**
   * Reset link-out UI state on logout so the same deep link can open Add Request again in a new session.
   */
  clearLinkoutFlowState(): void {
    clearLinkoutFlowStateStorage();
  }

  /**
   * User closed Add Request without submitting — notify backend (best-effort).
   * Adjust path if your API uses a different route or verb.
   */
  notifyLinkoutRejected(correlationId: string): Observable<unknown> {
    return this.http.post<unknown>(`${environment.WebApiURL}api/linkout/Reject`, { correlationId });
  }

  setPendingAddRequest(payload: LinkoutAddRequestPayload): void {
    sessionStorage.setItem(PENDING_DIALOG_KEY, JSON.stringify(payload));
  }

  consumePendingAddRequest(): LinkoutAddRequestPayload | null {
    const raw = sessionStorage.getItem(PENDING_DIALOG_KEY);
    if (!raw) {
      return null;
    }
    sessionStorage.removeItem(PENDING_DIALOG_KEY);
    try {
      return JSON.parse(raw) as LinkoutAddRequestPayload;
    } catch {
      return null;
    }
  }

  peekPendingAddRequest(): LinkoutAddRequestPayload | null {
    const raw = sessionStorage.getItem(PENDING_DIALOG_KEY);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as LinkoutAddRequestPayload;
    } catch {
      return null;
    }
  }

  /**
   * After Success, navigate to RedirectUrl (e.g. /linkout?correlationId=&claimNumber=); next init opens Add Request without a second API call.
   */
  prepareOpenAddRequestDialogAfterNavigation(correlationId: string, claimNumber: string): void {
    sessionStorage.setItem(OPEN_ADD_DIALOG_KEY, JSON.stringify({ correlationId, claimNumber }));
  }

  tryConsumeOpenAddRequestDialog(correlationId: string, claimNumber: string): boolean {
    const raw = sessionStorage.getItem(OPEN_ADD_DIALOG_KEY);
    if (!raw) {
      return false;
    }
    try {
      const p = JSON.parse(raw) as { correlationId?: string; claimNumber?: string };
      if (p.correlationId === correlationId && p.claimNumber === claimNumber) {
        sessionStorage.removeItem(OPEN_ADD_DIALOG_KEY);
        return true;
      }
    } catch {
      /* ignore */
    }
    return false;
  }

  /**
   * Full URL for browser navigation to api/linkout/Redirect (include returnUrl like Entry/resolve).
   * Use window.location.href — do not use HttpClient: OAuth 302 chains hit CORS when followed as XHR.
   */
  getLinkoutRedirectBrowserUrl(correlationId: string, claimNumber: string, returnUrl: string): string {
    const base = `${environment.WebApiURL}api/linkout/Redirect`;
    return `${base}?correlationId=${encodeURIComponent(correlationId)}&claimNumber=${encodeURIComponent(claimNumber)}&returnUrl=${encodeURIComponent(returnUrl)}`;
  }

  /**
   * GET api/linkout/Redirect (XHR). Prefer {@link getLinkoutRedirectBrowserUrl} + window.location when the API may 302 to SSO.
   */
  linkoutRedirect(correlationId: string, claimNumber: string, returnUrl?: string): Observable<LinkoutRedirectApiResponse | null> {
    const base = `${environment.WebApiURL}api/linkout/Redirect`;
    let url = `${base}?correlationId=${encodeURIComponent(correlationId)}&claimNumber=${encodeURIComponent(claimNumber)}`;
    if (returnUrl != null && String(returnUrl).trim() !== '') {
      url += `&returnUrl=${encodeURIComponent(String(returnUrl).trim())}`;
    }
    return this.http.get<LinkoutRedirectApiResponse | null>(url);
  }
}
